/**
 * Admin V2 Permissions Configuration
 * 
 * Defines which admin roles can access which pages and features.
 * This ensures proper role-based access control throughout the admin panel.
 */

import { AdminRole, NavigationItem, AdminPageConfig } from '../types/admin-v2';

/**
 * Page access permissions for different admin roles
 * Note: Gordian admin role = 'admin', Company admin role = 'useradmin'
 */
export const ADMIN_PAGE_PERMISSIONS = {
  // Pages accessible by both Gordian Admin (admin) and Company Admin (useradmin)
  shared: [
    'customer-list',
    'supplier-list',
    'product-list',
    'open-rfqs',
    'closed-rfqs'
  ],

  // Pages accessible only by Gordian Admin (role: 'admin')
  admin_only: [
    'system-settings',
    'user-management',
    'company-management',
    'global-analytics',
    'billing-management',
    'agentic'
  ],

  // Pages accessible only by Company Admin (role: 'useradmin')
  useradmin_only: [
    'company-settings',
    'team-management',
    'company-analytics',
    'company-billing'
  ]
} as const;

/**
 * Navigation items configuration
 */
export const ADMIN_NAVIGATION_ITEMS: NavigationItem[] = [
  {
    id: 'customer-list',
    label: 'Customer List',
    route: '/dashboard/customers',
    icon: '●',
    permissions: ['admin', 'useradmin']
  },
  {
    id: 'supplier-list',
    label: 'Supplier List',
    route: '/dashboard/suppliers',
    icon: '●',
    permissions: ['admin', 'useradmin']
  },
  {
    id: 'product-list',
    label: 'Product List',
    route: '/dashboard/products',
    icon: '●',
    permissions: ['admin', 'useradmin'],
    children: [
      {
        id: 'product-details',
        label: 'Product Details',
        route: '/dashboard/products/details',
        icon: '●',
        permissions: ['admin', 'useradmin']
      },
      {
        id: 'manual-products',
        label: 'Manual Products',
        route: '/dashboard/products/manual-products',
        icon: '●',
        permissions: ['admin', 'useradmin']
      }
    ]
  },
  {
    id: 'open-rfqs',
    label: 'Open RFQs',
    route: '', // No route - only dropdown
    icon: '●',
    permissions: ['admin', 'useradmin'],
    children: [
      {
        id: 'open-rfqs-pro',
        label: 'Pro RFQs',
        route: '/dashboard/rfqs/open?plan=pro',
        icon: '●',
        permissions: ['admin', 'useradmin']
      },
      {
        id: 'open-rfqs-plus',
        label: 'Plus RFQs',
        route: '/dashboard/rfqs/open?plan=plus',
        icon: '●',
        permissions: ['admin', 'useradmin']
      },
      {
        id: 'open-rfqs-basic',
        label: 'Basic RFQs',
        route: '/dashboard/rfqs/open?plan=basic',
        icon: '●',
        permissions: ['admin', 'useradmin']
      }
    ]
  },
  {
    id: 'finalized-rfqs',
    label: 'Finalized RFQs',
    route: '', // No route - only dropdown
    icon: '●',
    permissions: ['admin', 'useradmin'],
    children: [
      {
        id: 'finalized-rfqs-pro',
        label: 'Pro',
        route: '/dashboard/rfqs/finalized?plan=pro',
        icon: '●',
        permissions: ['admin', 'useradmin']
      },
      {
        id: 'finalized-rfqs-plus',
        label: 'Plus',
        route: '/dashboard/rfqs/finalized?plan=plus',
        icon: '●',
        permissions: ['admin', 'useradmin']
      },
      {
        id: 'finalized-rfqs-basic',
        label: 'Basic',
        route: '/dashboard/rfqs/finalized?plan=basic',
        icon: '●',
        permissions: ['admin', 'useradmin']
      }
    ]
  },
  {
    id: 'system-settings',
    label: 'System Settings',
    route: '/dashboard/settings/system',
    icon: '●',
    permissions: ['admin']
  },
  {
    id: 'inbox',
    label: 'Inbox',
    route: '/dashboard/inbox',
    permissions: ['admin'],
    children: [
      {
        id: 'inbox-old',
        label: 'Old Mails',
        route: '/dashboard/inbox/old',
        icon: '●',
        permissions: ['admin']
      }
    ]
  },
  {
    id: 'agentic',
    label: 'Agentic',
    route: '/dashboard/agentic',
    icon: '●',
    permissions: ['admin']
  },
  {
    id: 'request-audit-logs',
    label: 'Audit Logs',
    route: '/dashboard/request-audit-logs',
    icon: '●',
    permissions: ['admin']
  }
];

/**
 * Default page routing for each admin role
 * Note: Gordian admin role = 'admin', Company admin role = 'useradmin'
 */
export const getDefaultPageForRole = (role: AdminRole): string => {
  const defaultPages = {
    admin: '/dashboard/customers',      // Gordian admin
    useradmin: '/dashboard/customers'   // Company admin
  };
  return defaultPages[role];
};

/**
 * Check if a role has permission to access a specific page
 */
export const hasPagePermission = (role: AdminRole, pageId: string): boolean => {
  const allSharedPages = ADMIN_PAGE_PERMISSIONS.shared;
  const roleSpecificPages = role === 'admin'
    ? ADMIN_PAGE_PERMISSIONS.admin_only
    : ADMIN_PAGE_PERMISSIONS.useradmin_only;

  return (allSharedPages as readonly string[]).includes(pageId) ||
    (roleSpecificPages as readonly string[]).includes(pageId);
};

/**
 * Get navigation items filtered by user role
 */
export const getNavigationItemsForRole = (role: AdminRole): NavigationItem[] => {
  return ADMIN_NAVIGATION_ITEMS.filter(item =>
    item.permissions.includes(role)
  ).map(item => {
    // Filter children by permissions as well
    if (item.children && item.children.length > 0) {
      return {
        ...item,
        children: item.children.filter(child =>
          child.permissions.includes(role)
        )
      };
    }
    return item;
  });
};
